/**
 * The state filter — one dropdown, used by every surface that scopes a
 * list of accounts to states.
 *
 * It lived inside FollowUpTracker, and when the No Shipments Yet tab
 * needed the same filter it got a second, plainer implementation. Two
 * dropdowns doing the same job in the same page, looking different, is
 * how a product starts to feel assembled rather than designed — so
 * there is now one.
 *
 * What it carries that a bare Select does not:
 *
 *   • Two labelled groups. A rep's own states come first under "My
 *     territory"; everything else follows under "Other states". A rep
 *     working away from home needs the other states, but not ahead of
 *     their own.
 *   • Check affordances. An empty outlined square on every row says
 *     "more than one of these can be on" before you click anything.
 *   • Control-state flags. CTRL marks a state whose board controls
 *     distribution, which changes what a visit can achieve.
 *   • A reset. Selecting a state is one click; clearing it means
 *     remembering what you picked. The header carries the way back, and
 *     the caller names it — "Clear" where empty means all states, "Back
 *     to my territory" where empty means the rep's own.
 */

import CheckIcon from '@mui/icons-material/Check';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';

import { isControlState } from '../lib/controlStates';
import { colors } from '../theme';

interface StateFilterSelectProps {
  /** Currently selected states. Empty means "no filter". */
  value: string[];
  onChange: (next: string[]) => void;
  /** Every state that can be picked, in display order. */
  states: string[];
  /**
   * The rep's own states, listed first. Empty (or an admin, who has no
   * territory) collapses the two groups into one unlabelled list —
   * a "My territory" heading with nothing under it helps nobody.
   */
  territoryStates?: string[];
  /** Trigger text when nothing is selected. */
  emptyLabel: string;
  /** Label for the header's reset control. */
  resetLabel: string;
}

export function StateFilterSelect({
  value,
  onChange,
  states,
  territoryStates,
  emptyLabel,
  resetLabel,
}: StateFilterSelectProps) {
  const own = new Set(territoryStates ?? []);
  const mine = territoryStates?.length ? states.filter((s) => own.has(s)) : [];
  const others = territoryStates?.length ? states.filter((s) => !own.has(s)) : states;

  const renderGroup = (group: string[], label: string | null) => {
    if (group.length === 0) return null;
    return [
      label ? (
        <ListSubheader
          key={`hdr-${label}`}
          disableSticky
          sx={{
            px: 1.5,
            py: 0.5,
            lineHeight: 1.6,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            bgcolor: 'transparent',
          }}
        >
          {label}
        </ListSubheader>
      ) : null,
      ...group.map((s) => {
        const checked = value.includes(s);
        return (
          <MenuItem
            key={s}
            value={s}
            sx={{
              py: 0.55,
              px: 1.5,
              minHeight: 'unset',
              fontSize: 13,
              gap: 1.25,
              bgcolor: 'transparent',
              transition: 'background-color 0.12s',
              '&:hover, &.Mui-focusVisible': {
                bgcolor: checked ? `${colors.chartPrimary}14` : colors.surfaceSubtle,
                '& .check-affordance': {
                  borderColor: checked ? colors.chartPrimary : colors.textMuted,
                },
              },
              '&.Mui-selected': {
                bgcolor: `${colors.chartPrimary}0d`,
                '&:hover, &.Mui-focusVisible': { bgcolor: `${colors.chartPrimary}14` },
              },
            }}
          >
            <Box
              className="check-affordance"
              sx={{
                width: 16,
                height: 16,
                borderRadius: 0.75,
                flexShrink: 0,
                border: `1.5px solid ${checked ? colors.chartPrimary : colors.border}`,
                bgcolor: checked ? colors.chartPrimary : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.12s',
                boxShadow: checked ? `0 0 0 3px ${colors.chartPrimary}1f` : 'none',
              }}
            >
              {checked && <CheckIcon sx={{ fontSize: 12, color: '#fff', strokeWidth: 3 }} />}
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: checked ? 600 : 500,
                color: checked ? colors.chartPrimary : colors.textPrimary,
                letterSpacing: '0.01em',
                flexGrow: 1,
              }}
            >
              {s}
            </Typography>
            {isControlState(s) && (
              <Tooltip
                title="Control state — state board controls distribution"
                arrow
                placement="right"
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: '#7c3aed',
                    bgcolor: '#7c3aed14',
                    border: '1px solid #7c3aed44',
                    borderRadius: 0.5,
                    px: 0.6,
                    py: 0.15,
                    lineHeight: 1.4,
                    flexShrink: 0,
                  }}
                >
                  CTRL
                </Box>
              </Tooltip>
            )}
          </MenuItem>
        );
      }),
    ];
  };

  return (
    <FormControl size="small" sx={{ minWidth: 170 }}>
      <Select
        multiple
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(typeof v === 'string' ? v.split(',') : v);
        }}
        displayEmpty
        renderValue={(selected) => {
          const sel = selected as string[];
          let label: string;
          if (sel.length === 0) {
            label = emptyLabel;
          } else if (sel.length === 1) {
            label = `State: ${sel[0]}`;
          } else if (sel.length <= 3) {
            label = `States: ${[...sel].sort().join(', ')}`;
          } else {
            label = `${sel.length} states selected`;
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <FilterListIcon sx={{ fontSize: 16, color: colors.textMuted }} />
              <Typography
                sx={{
                  fontSize: 13,
                  color: sel.length > 0 ? colors.textPrimary : colors.textSecondary,
                  fontWeight: sel.length > 0 ? 600 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 220,
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 380,
              mt: 0.75,
              minWidth: 200,
              borderRadius: 1.5,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
              bgcolor: '#fff',
              // Hide the MenuList's default padding so the sticky header
              // sits flush at the top.
              '& .MuiMenu-list, & .MuiList-root': { py: 0 },
            },
          },
        }}
        sx={{ '& .MuiSelect-select': { py: 0.85 } }}
      >
        {/* Sticky header: eyebrow label + selected count + reset.
            Wrapped in a MenuItem so MUI keeps the keyboard focus path
            intact, but styled so it doesn't look like a selectable row. */}
        <MenuItem
          disableRipple
          disableGutters
          sx={{
            px: 1.5,
            py: 1,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: '#fff',
            borderBottom: `1px solid ${colors.borderLight}`,
            cursor: 'default',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            '&:hover': { bgcolor: '#fff' },
          }}
          // Defensive: even though pointer-events is none, also block
          // selection if a stray click somehow makes it through.
          value=""
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: colors.textMuted,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {value.length === 0 ? 'Filter by state' : `${value.length} selected`}
            </Typography>
            {value.length === 0 && (
              <Typography
                sx={{
                  fontSize: 10,
                  color: colors.textMuted,
                  opacity: 0.7,
                  fontStyle: 'italic',
                  whiteSpace: 'nowrap',
                }}
              >
                · select multiple
              </Typography>
            )}
          </Box>
          {value.length > 0 && (
            <Box
              component="span"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }}
              sx={{
                pointerEvents: 'auto',
                fontSize: 11.5,
                fontWeight: 600,
                color: colors.chartPrimary,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                transition: 'background-color 0.12s',
                '&:hover': { bgcolor: `${colors.chartPrimary}10` },
              }}
            >
              {resetLabel}
            </Box>
          )}
        </MenuItem>

        {/* One renderer, two groups. The markup is identical; only the
            heading above each list differs. */}
        {renderGroup(mine, territoryStates?.length ? 'My territory' : null)}
        {renderGroup(others, territoryStates?.length ? 'Other states' : null)}
      </Select>
    </FormControl>
  );
}
